import React from "react";

import { createAccordionItems, isFootprintReadOnly, useGetFootprintingPigletFootprint, useNavigate, useTranslation, useWasAutofilled } from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { PigletFeedInformationForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletFeedInformationForm";
import { PigletGettingStartedForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletGettingStartedForm";
import { PigletHerdDetailsForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletHerdDetailsForm";
import { PigletManureAdditiveForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletManureAdditiveForm";
import { PigletManureStorageForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletManureStorageForm";
import { PigletMoorlandForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletMoorlandForm";
import { PigletStableTypeForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletStableTypeForm";
import { PigletYieldForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletYieldForm";
import { PigletCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface PigletFlowProps {
  farmId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
}

const pigletFormCategories = {
  animals: "footprinting:footprintingLandingPage.assessmentCategories.piglet.animalsBreed",
  feed: "footprinting:footprintingLandingPage.assessmentCategories.piglet.feed",
  manure: "footprinting:footprintingLandingPage.assessmentCategories.piglet.manure",
};

export const PigletFormFlow = ({ farmId, year, initialStep, editMode, enablePageTransitions }: PigletFlowProps) => {
  const { t } = useTranslation("footprintingPiglet");
  const navigate = useNavigate();
  const pigletProgressQuery = useGetFootprintingPigletFootprint(farmId, year);
  const readOnly = isFootprintReadOnly(pigletProgressQuery.data);
  const wasAutofilled = useWasAutofilled("piglet", farmId, year);
  const [currentStep, setCurrentStep] = React.useState<number | undefined>(initialStep ?? 0);

  const getPageStatus = (pageKey: string) => {
    if (pigletProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (pigletProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const pigletFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.piglet.gettingStarted"),
      key: PigletGettingStartedForm.Key,
      status: getPageStatus(PigletGettingStartedForm.Key),
      pageSubtitle: t("pigletGettingStartedForm.subTitle"),
      body: () => <PigletGettingStartedForm primaryDataId={pigletProgressQuery.data?.footprint?.id} />,
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.yield"),
      key: PigletYieldForm.Key,
      status: getPageStatus(PigletYieldForm.Key),
      pageSubtitle: t("pigletYieldForm.subTitle"),
      body: () => <PigletYieldForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "yield"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.herdDetails"),
      navigationCategory: t(pigletFormCategories.animals),
      key: PigletHerdDetailsForm.Key,
      status: getPageStatus(PigletHerdDetailsForm.Key),
      pageSubtitle: t("pigletHerdDetailsForm.subTitle"),
      body: () => <PigletHerdDetailsForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "herdDetails"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.stableType"),
      navigationCategory: t(pigletFormCategories.animals),
      key: PigletStableTypeForm.Key,
      status: getPageStatus(PigletStableTypeForm.Key),
      pageSubtitle: t("pigletStableTypeForm.subTitle"),
      body: () => <PigletStableTypeForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "stableType"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.feedInformation"),
      navigationCategory: t(pigletFormCategories.feed),
      key: PigletFeedInformationForm.Key,
      status: getPageStatus(PigletFeedInformationForm.Key),
      pageSubtitle: t("pigletFeedInformationForm.subTitle"),
      body: () => <PigletFeedInformationForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "feedInformation"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.manureStorage"),
      navigationCategory: t(pigletFormCategories.manure),
      key: PigletManureStorageForm.Key,
      status: getPageStatus(PigletManureStorageForm.Key),
      pageSubtitle: t("pigletManureStorageForm.subTitle"),
      body: () => <PigletManureStorageForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "manureStorage"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.manureAdditive"),
      navigationCategory: t(pigletFormCategories.manure),
      key: PigletManureAdditiveForm.Key,
      status: getPageStatus(PigletManureAdditiveForm.Key),
      pageSubtitle: t("pigletManureAdditiveForm.subTitle"),
      body: () => <PigletManureAdditiveForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "manureAdditive"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.moorland"),
      key: PigletMoorlandForm.Key,
      status: getPageStatus(PigletMoorlandForm.Key),
      pageSubtitle: t("pigletMoorlandForm.info"),
      body: () => <PigletMoorlandForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPiglet", "moorland"),
    },
    {
      navigationTitle: t("footprinting:navigation.piglet.comment"),
      key: PigletCommentForm.Key,
      status: getPageStatus(PigletCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <PigletCommentForm readOnly={readOnly} primaryDataId={pigletProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  return (
    <FormFlowScreen
      formPages={pigletFlowSteps}
      initialStep={currentStep}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      completionPercent={pigletProgressQuery.data?.completionPct ?? 0}
      navBarTitle={t("footprinting:navigation.piglet.navBarTitle", { year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={pigletProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
